import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, ArrowUp, Plus, GripVertical } from 'lucide-react'
import { PDFDocument } from 'pdf-lib'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface PDFFile {
  id: string
  file: File
  name: string
  pageCount?: number
  size: number
  status: 'pending' | 'loaded' | 'error'
}

const PDFMerger: React.FC = () => {
  const [pdfFiles, setPdfFiles] = useState<PDFFile[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [mergedPdf, setMergedPdf] = useState<Uint8Array | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf'
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: PDFFile[] = []
    
    for (const file of pdfFiles) {
      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer)
        const pageCount = pdfDoc.getPageCount()
        
        newFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          name: file.name,
          pageCount,
          size: file.size,
          status: 'loaded'
        })
      } catch (error) {
        newFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          name: file.name,
          size: file.size,
          status: 'error'
        })
        toast.error(`Failed to load ${file.name}`)
      }
    }

    setPdfFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${newFiles.filter(f => f.status === 'loaded').length} PDF(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const removePdf = (id: string) => {
    setPdfFiles(prev => prev.filter(pdf => pdf.id !== id))
    setMergedPdf(null)
  }

  const movePdf = (id: string, direction: 'up' | 'down') => {
    setPdfFiles(prev => {
      const index = prev.findIndex(pdf => pdf.id === id)
      if (index === -1) return prev
      
      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= prev.length) return prev
      
      const newArray = [...prev]
      const [movedItem] = newArray.splice(index, 1)
      newArray.splice(newIndex, 0, movedItem)
      return newArray
    })
    setMergedPdf(null)
  }

  // Drag and Drop Functions
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', id)
    
    // Add visual feedback to the dragged element
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5'
    }
  }

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedId(null)
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1'
    }
  }

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    const draggedItemId = draggedId

    if (!draggedItemId || draggedItemId === targetId) {
      setDraggedId(null)
      return
    }

    setPdfFiles(prev => {
      const draggedIndex = prev.findIndex(pdf => pdf.id === draggedItemId)
      const targetIndex = prev.findIndex(pdf => pdf.id === targetId)
      
      if (draggedIndex === -1 || targetIndex === -1) return prev
      
      const newArray = [...prev]
      const [draggedItem] = newArray.splice(draggedIndex, 1)
      newArray.splice(targetIndex, 0, draggedItem)
      
      return newArray
    })
    
    setDraggedId(null)
    setMergedPdf(null)
    toast.success('PDF order updated!')
  }

  const mergePdfs = async () => {
    if (pdfFiles.length < 2) {
      toast.error('Please select at least 2 PDF files to merge')
      return
    }

    setIsProcessing(true)
    
    try {
      const mergedPdf = await PDFDocument.create()
      
      for (const pdfFile of pdfFiles) {
        if (pdfFile.status !== 'loaded') continue
        
        const arrayBuffer = await pdfFile.file.arrayBuffer()
        const pdf = await PDFDocument.load(arrayBuffer)
        const pageIndices = pdf.getPageIndices()
        
        const pages = await mergedPdf.copyPages(pdf, pageIndices)
        pages.forEach((page) => mergedPdf.addPage(page))
      }
      
      const pdfBytes = await mergedPdf.save()
      setMergedPdf(pdfBytes)
      toast.success('PDFs merged successfully!')
      
    } catch (error) {
      console.error('Error merging PDFs:', error)
      toast.error('Failed to merge PDFs')
    } finally {
      setIsProcessing(false)
    }
  }

  const downloadMergedPdf = () => {
    if (!mergedPdf) return

    const blob = new Blob([new Uint8Array(mergedPdf)], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'merged-document.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const totalPages = pdfFiles
    .filter(pdf => pdf.status === 'loaded')
    .reduce((sum, pdf) => sum + (pdf.pageCount || 0), 0)

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF Merger
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Combine multiple PDF files into a single document. Drag to reorder files and preserve document structure.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfMerger.header}
          className="mb-8"
        />

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`card border-2 border-dashed cursor-pointer transition-colors mb-8 ${
            isDragActive ? 'border-[#8438ff] bg-[#8438ff]/10' : 'border-gray-300 hover:border-[#8438ff]'
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-center py-12">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-xl font-medium text-[#8438ff] mb-2">
              {isDragActive ? 'Drop PDF files here' : 'Choose PDFs or drag them here'}
            </p>
            <p className="text-gray-600">
              Select multiple PDF files to merge into one document
            </p>
          </div>
        </div>

        {/* Content Ad - appears when files are uploaded */}
        {pdfFiles.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfMerger.content}
            className="mb-8"
          />
        )}

        {/* PDF List */}
        {pdfFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-[#8438ff]">PDF Files ({pdfFiles.length})</h3>
                <p className="text-sm text-gray-600">Total pages: {totalPages}</p>
                <p className="text-xs text-[#8438ff] mt-1">
                  💡 Drag files to reorder or use arrow buttons
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={mergePdfs}
                  disabled={pdfFiles.length < 2 || isProcessing}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Merge PDFs
                    </>
                  )}
                </button>
                
                {mergedPdf && (
                  <button
                    onClick={downloadMergedPdf}
                    className="btn btn-success"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Merged PDF
                  </button>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {pdfFiles.map((pdf, index) => (
                <motion.div
                  key={pdf.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, pdf.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, pdf.id)}
                  onDrop={(e) => handleDrop(e, pdf.id)}
                  className={`card flex items-center justify-between cursor-move transition-all duration-200 ${
                    pdf.status === 'error' ? 'border-red-200 bg-red-50' : ''
                  } ${
                    draggedId === pdf.id ? 'scale-105 shadow-lg border-[#8438ff]' : ''
                  } hover:shadow-md hover:border-[#8438ff]/50`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Drag Handle */}
                    <div className="drag-handle p-1 text-gray-400 hover:text-[#8438ff] cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-5 w-5" />
                    </div>
                    
                    {/* Move Buttons */}
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => movePdf(pdf.id, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-[#8438ff] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => movePdf(pdf.id, 'down')}
                        disabled={index === pdfFiles.length - 1}
                        className="p-1 text-gray-400 hover:text-[#8438ff] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ArrowUp className="h-3 w-3 rotate-180" />
                      </button>
                    </div>
                    
                    {/* Order Number */}
                    <div className="bg-[#8438ff]/10 p-2 rounded-lg border border-[#8438ff]/20">
                      <span className="text-sm font-bold text-[#8438ff]">#{index + 1}</span>
                    </div>
                    
                    {/* File Icon */}
                    <FileText className={`h-8 w-8 ${pdf.status === 'error' ? 'text-red-400' : 'text-[#8438ff]'}`} />
                    
                    {/* File Info */}
                    <div>
                      <p className={`font-medium ${pdf.status === 'error' ? 'text-red-900' : 'text-[#8438ff]'}`}>
                        {pdf.name}
                      </p>
                      <p className={`text-sm ${pdf.status === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                        {pdf.status === 'error' ? 'Failed to load' : 
                         `${pdf.pageCount} pages • ${formatFileSize(pdf.size)}`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removePdf(pdf.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove PDF"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Success Message */}
        {mergedPdf && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card bg-green-50 border-green-200"
          >
            <div className="text-center py-6">
              <div className="text-green-600 mb-2">
                <FileText className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                PDF Merged Successfully!
              </h3>
              <p className="text-green-700 mb-4">
                Your {pdfFiles.length} PDF files have been combined into one document with {totalPages} pages.
              </p>
              <button
                onClick={downloadMergedPdf}
                className="btn btn-success"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Merged PDF
              </button>
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfMerger.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PDFMerger
